import posthog from 'posthog-js';
import { env } from '@/lib/env';
import {
  analyticsAllowed,
  consentDecision,
  refreshConsent,
  type ConsentDecision,
} from '@/lib/consent';

// PostHog, gated on the guest's consent (OH-72). The three states are defined
// in lib/consent.ts; this module is where each one becomes a configuration.
//
// We do NOT call posthog.identify for anonymous visitors (ADR-028) —
// person_profiles: 'identified_only' keeps them to a session id.
let initialized = false;

// The settings that differ between "consented" and "silenced", named once so
// the two directions cannot drift apart. They did drift: the denied path
// turned three things off and the granted path turned none of them back on,
// so a guest who rejected and then reconsidered ended up with an accepted
// consent and memory-only persistence — the cross-visit funnel they had just
// agreed to did not exist, and nothing said so.
const FULL_MODE = {
  capture_pageleave: true,
  // Real-guest FCP/LCP/CLS/INP (posthog-js's allowedMetrics defaults to all
  // four — nothing here narrows it). A one-off Lighthouse number says the page
  // is fast today; this says whether it stays fast, on the devices and
  // networks guests actually use. Supported in cookieless mode: the metrics
  // arrive without a client session id and ingestion assigns one.
  capture_performance: { web_vitals: true },
  persistence: 'localStorage+cookie',
} as const;

const SILENCED_MODE = {
  capture_pageleave: false,
  capture_performance: false,
  persistence: 'memory',
  // /flags is NOT a capture, so before_send never sees it. posthog.reset()
  // ends by reloading feature flags, and remote config installs a refresh
  // every five minutes — so without this a guest who rejects POSTs their entry
  // URL, referrer, timezone and a fresh device id to PostHog moments after the
  // click, and again every five minutes for as long as the tab stays open.
  advanced_disable_flags: true,
} as const;

// SECRET_PARAMS are query parameters that are live credentials. Since OH-149
// the magic link lands on OUR origin (/auth/magic?token=…), so for the first
// moments of that page the token is in window.location — and posthog-js reads
// window.location for $current_url on every event it sends, ours and its own.
// While the link pointed at the API origin this could not happen; now it can.
const SECRET_PARAMS = ['token', 'code'];

// scrubUrl removes the values of SECRET_PARAMS from a URL string. Non-URLs and
// anything unparseable come back untouched — this is a redactor, not a
// validator, and dropping a property we failed to parse would silently lose
// data that is not sensitive.
function scrubUrl(value: unknown): unknown {
  if (typeof value !== 'string' || !value.includes('?')) return value;
  try {
    const url = new URL(value, window.location.origin);
    let touched = false;
    for (const p of SECRET_PARAMS) {
      if (url.searchParams.has(p)) {
        url.searchParams.set(p, 'redacted');
        touched = true;
      }
    }
    return touched ? url.toString() : value;
  } catch {
    return value;
  }
}

// stripCredentialsFromUrls redacts credentials from every URL-bearing property
// PostHog attaches.
//
// $set_once.$initial_current_url is the one that matters most and is easiest
// to miss: it is captured at init and written to PERSON properties, so a guest
// whose session begins on the magic-link page would carry their sign-in token
// in their profile long after the token itself expired. Stripping the URL in
// the page component (which we also do) happens a beat too late for it.
export function stripCredentialsFromUrls<T extends { properties?: Record<string, unknown> } | null>(
  event: T,
): T {
  const props = event?.properties;
  if (!props) return event;

  for (const key of ['$current_url', '$referrer', '$pathname']) {
    if (key in props) props[key] = scrubUrl(props[key]);
  }
  for (const bag of ['$set', '$set_once']) {
    const nested = props[bag];
    if (nested && typeof nested === 'object') {
      const record = nested as Record<string, unknown>;
      for (const key of Object.keys(record)) {
        if (key.toLowerCase().includes('url') || key.toLowerCase().includes('referr')) {
          record[key] = scrubUrl(record[key]);
        }
      }
    }
  }
  return event;
}

export function initPostHog(): void {
  if (initialized) return;
  if (!env.posthogKey) return;

  const decision = consentDecision();
  const granted = decision === 'granted';

  // WITHOUT A CURRENT "YES", CLEAR FIRST. PostHog's own opt-in flag lives in
  // localStorage and never expires; our consent cookie lasts six months, and
  // Safari caps a script-written cookie at seven days. So a guest who accepted
  // last week can come back with the banner showing — all three of our gates
  // reading "no choice yet" — while `__ph_opt_in_out_<token>` still says 1,
  // which makes posthog-js skip cookieless mode entirely and resume tracking
  // under the identifier it kept. Consent that expired has to expire on both
  // sides. The same sweep is what makes "denied ⇒ nothing on the device" true
  // on every load rather than only at the moment of the click.
  if (!granted) clearAnalyticsStorage();

  // Denied: do not initialise at all. Not "initialise and suppress" — there is
  // then nothing running that could write to the device through a config we
  // forgot about, and the assertion "no PostHog storage after Reject" holds by
  // construction rather than by vigilance.
  if (decision === 'denied') {
    watchForCrossTabChanges();
    return;
  }

  posthog.init(env.posthogKey, {
    api_host: env.posthogHost ?? 'https://eu.i.posthog.com',
    capture_pageview: false,
    autocapture: false,
    person_profiles: 'identified_only',
    capture_pageleave: FULL_MODE.capture_pageleave,
    capture_performance: FULL_MODE.capture_performance,
    // Nothing in this app reads a feature flag (no isFeatureEnabled /
    // getFeatureFlag / onFeatureFlags anywhere under src/), so the /flags
    // round-trip buys nothing and costs a request carrying the visitor's entry
    // URL and referrer — sent, in the pending state, before they have agreed
    // to anything.
    advanced_disable_flags: true,
    ...(granted
      ? {}
      : {
          // BEFORE A CHOICE. cookieless_mode makes PostHog write nothing to the
          // device — no cookie, no localStorage — and derive identity from a
          // rotating hash computed on its servers. 'on_reject' ties that mode
          // to the consent manager's opt-out state, and
          // opt_out_capturing_by_default puts us in it from the first event.
          // Accept lifts both, via applyConsent below.
          //
          // This mode is INERT unless the PostHog PROJECT has cookieless
          // enabled (Project settings → cookieless_server_hash_mode). With it
          // off, every pre-consent event is dropped at ingestion and nothing
          // anywhere says so — the failure looks exactly like "guests stopped
          // visiting". Enabled as stateful (2) on both projects (Ollio 182241,
          // Booking 218401) on 2026-08-22; stateful rather than stateless
          // because stateless cannot carry a session, and the whole funnel
          // OH-71 baselined happens inside one.
          cookieless_mode: 'on_reject' as const,
          opt_out_capturing_by_default: true,
        }),
    // THE GATE for captured events. "Reject means silence" has to cover the
    // events this codebase never names — $pageleave and the web vitals
    // posthog-js captures on its own — so it is enforced at the library
    // boundary rather than in analytics.ts, which only knows about ours. Reads
    // the LIVE decision, so a Reject clicked mid-session stops the very next
    // event. It does NOT cover /flags, which is why SILENCED_MODE exists.
    before_send: (event) =>
      consentDecision() === 'denied' ? null : stripCredentialsFromUrls(event),
  });
  initialized = true;
  watchForCrossTabChanges();
}

// applyConsent moves an ALREADY RUNNING session to the choice just made, so a
// guest who accepts does not have to reload to be counted properly.
export function applyConsent(decision: ConsentDecision): void {
  if (!env.posthogKey) return;

  if (decision === 'granted') {
    if (!initialized) {
      // Arrived with a stored "denied", reopened the footer panel, accepted:
      // nothing is running, so start it now — and initPostHog re-reads the
      // decision, so it starts in full mode.
      initPostHog();
      return;
    }
    // Undo everything SILENCED_MODE turned off. Reaching here after a Reject
    // in the same session is not a corner case: it is the Art. 7(3) path the
    // footer link exists to provide.
    posthog.set_config({ ...FULL_MODE });
    // captureEventName:false — an "$opt_in" event would be the first thing in
    // this person's history and means nothing to the booking funnel.
    posthog.opt_in_capturing({ captureEventName: false });
    return;
  }

  if (decision === 'denied') {
    if (initialized) {
      // ORDER IS THE WHOLE POINT HERE, and it was learned the hard way:
      // clearing the storage first leaves posthog-js still pointed at
      // localStorage, and the next thing that touches persistence — a dropped
      // capture still updates session properties before before_send ever sees
      // it — writes the key straight back. The e2e assertion caught exactly
      // that: the identifier reappeared after a Reject, intermittently.
      //
      // So: silence the library FIRST (nothing further can reach the device or
      // the network), then reset the in-memory identity, and only then delete
      // what is already on disk.
      posthog.set_config({ ...SILENCED_MODE });
      posthog.reset(true);
    }
    clearAnalyticsStorage();
  }
}

// A decision belongs to the browser, not to one document. Re-read it whenever
// this tab comes back to the front, and act on a change — otherwise a guest
// who withdraws consent in one tab leaves another tab still holding a fully
// persistent PostHog, which writes the identifier straight back. Withdrawal
// confirmed on screen, undone in the background.
let watching = false;
function watchForCrossTabChanges(): void {
  if (watching || typeof window === 'undefined') return;
  watching = true;
  const recheck = () => {
    if (document.visibilityState === 'hidden') return;
    const changed = refreshConsent();
    if (changed) applyConsent(changed);
  };
  document.addEventListener('visibilitychange', recheck);
  window.addEventListener('focus', recheck);
  // Returning through the back/forward cache — a guest coming back from
  // PayPal, for instance — restores the document without re-running any of
  // this module, so the decision has to be re-read there too.
  window.addEventListener('pageshow', recheck);
}

// clearAnalyticsStorage removes PostHog's own keys from the device.
//
// Prefix matching is right for a SWEEP and wrong for a CHECK, and the two are
// deliberately different here: this deletes what we can name, while the e2e
// assertion works by exclusion and fails on anything left behind. So if
// PostHog ever writes under a name outside these prefixes, a test says so
// rather than a guest quietly keeping a cookie they rejected.
function clearAnalyticsStorage(): void {
  if (typeof window === 'undefined') return;

  const isPostHogKey = (key: string) => key.startsWith('ph_') || key.startsWith('__ph_');

  for (const store of [window.localStorage, window.sessionStorage]) {
    try {
      for (const key of Object.keys(store)) {
        if (isPostHogKey(key)) store.removeItem(key);
      }
    } catch {
      // Storage can be unavailable (Safari private mode, blocked third-party
      // contexts). Nothing to clear there either, so this is not a failure.
    }
  }

  for (const part of document.cookie.split(';')) {
    const name = part.trim().split('=')[0];
    if (!name || !isPostHogKey(name)) continue;
    // posthog-js sets its cookie with cross_subdomain_cookie on by default,
    // so the same name can exist both host-only and on the parent domain.
    // Expiring only one of them leaves the other in place.
    const host = location.hostname;
    const parent = host.split('.').slice(-2).join('.');
    for (const domain of [undefined, host, `.${parent}`]) {
      const scope = domain ? `; domain=${domain}` : '';
      document.cookie = `${name}=; path=/; max-age=0${scope}`;
    }
  }
}

// identifyGuest links the current session to a known guest. Called once per
// login (ADR-028). Requires consent as well as initialisation: identifying is
// precisely the persistent, cross-visit linkage the guest would be consenting
// to, so doing it without a "yes" would defeat the gate from the inside.
export function identifyGuest(guestId: string): void {
  if (!initialized || !analyticsAllowed()) return;
  posthog.identify(guestId);
}

export { posthog };
